import {
  AfterViewInit,
  Component,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { TitleService } from '@app/core/services/title.service';
import { SessionService } from '@app/security/session.service';
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { TwoWayMessagingContainer } from '@app/two-way/relay-messenger/components/relay-messenger-container/RelayMessengerContainer';
import { ActivatedRoute, Router } from '@angular/router';
import { FeatureService } from '@app/core/services/feature.service';
import { FeatureFlags } from '@app/core/models/feature-flags';
import { ClientService } from '@app/core/services/client.service';
import { Client } from '@app/core/models/client';

export type RelayMessengerFeatureFlag = {
  flagName: string;
  flagValue: boolean;
};

@Component({
  selector: 'app-two-way-messaging',
  templateUrl: './relay-messenger.component.html',
  styleUrls: ['./relay-messenger.component.scss'],
  standalone: true,
})
export class RelayMessengerComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  client: Client;
  clientId: string = '';
  urlClientId: string | null = null;
  userId: string = '';
  userRoleIds: string[] = [];
  reactWrapperId: string = '';
  relayMessengerR2FeatureFlag: boolean = false;
  featureFlags = FeatureFlags;
  root: Root = null;
  rmAutoResponseFeatureFlag: boolean = false;
  rmTranslationsEnabled: boolean = false;
  rmPingTranslationsFeatureFlag: boolean = false;
  rmBulkSelectFeatureFlag: boolean = false;
  channelSid: string | null = null;

  constructor(
    private sessionService: SessionService,
    private titleService: TitleService,
    private route: ActivatedRoute,
    public featureService: FeatureService,
    private clientService: ClientService,
    private router: Router,
  ) { }

  ngAfterViewInit(): void {
    const node = document.getElementById(this.reactWrapperId);
    this.root = createRoot(node);
    this.render();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.render();
  }

  ngOnInit(): void {
    this.clientId = this.sessionService.getCurrentUsersClient().id;
    const params = this.getParamsFromUrl();
    this.urlClientId = params.clientId;
    this.channelSid = params.channelSid;

    // check for client id in the url and switch to that client if it is different or exists
    if (this.urlClientId && this.urlClientId !== this.clientId) {
      this.switchClientById(this.urlClientId);
    }
    this.titleService.activate('Relay Messenger');
    this.userId = this.sessionService.currentUser.id;
    this.userRoleIds = this.sessionService.currentUser.role_id;
    this.reactWrapperId = 'relay-messenger-wrapper-id';
    this.rmAutoResponseFeatureFlag = this.featureService.checkFlag(
      this.featureFlags.rm_auto_response,
    );
    this.rmPingTranslationsFeatureFlag = this.featureService.checkFlag(
      this.featureFlags.rm_ping_translations,
    );
    this.rmBulkSelectFeatureFlag = this.featureService.checkFlag(
      this.featureFlags.rm_bulk_select,
    );
    this.rmTranslationsEnabled =
      this.sessionService.getCurrentUsersClient().two_way_config?.translations_enabled;
  }

  getParamsFromUrl(): { clientId: string | null, channelSid: string | null } {
    let clientId: string | null = null;
    let channelSid: string | null = null;
    const wildcard = this.route.snapshot.params?.['**'];
    if (wildcard) {
      clientId = wildcard.split('/').filter(Boolean)[0] ?? null;
      channelSid = wildcard.split('/').filter(Boolean)[2] ?? null;
    } else {
      const basePath = '/two-way/relay-messenger/';
      const reactPath = this.router.url.startsWith(basePath)
        ? this.router.url.slice(basePath.length)
        : '';
      clientId = reactPath.split('/').filter(Boolean)[0] ?? null;
      channelSid = reactPath.split('/').filter(Boolean)[2] ?? null;
    }
    return { clientId, channelSid };
  }

  switchClientById(clientId: string) {
    return this.clientService.getClient(clientId).subscribe(
      (client) => {
        this.switchClients(client);
      },
      () => {
        this.client = new Client();
      },
    );
  }

  reloadSession() {
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigateByUrl(location.pathname);
    });
  }

  switchClients(client: Client) {
    this.sessionService.setClient(client);
    this.client = client;
    this.reloadSession();
  }

  ngOnDestroy(): void {
    this.titleService.deactivate();
    this.root.unmount();
  }

  protected render() {
    const featureFlags: RelayMessengerFeatureFlag[] = [
      {
        flagName: 'rm_auto_response',
        flagValue: this.rmAutoResponseFeatureFlag,
      },
      {
        flagName: 'rm_ping_translations',
        flagValue: this.rmPingTranslationsFeatureFlag,
      },
      {
        flagName: 'rm_bulk_select',
        flagValue: this.rmBulkSelectFeatureFlag,
      },
    ];
    if (this.root) {
      this.root.render(
        React.createElement(TwoWayMessagingContainer, {
          clientId: this.clientId,
          userId: this.userId,
          userRoleIds: this.userRoleIds,
          channelSid: this.channelSid,
          rmTranslationsEnabled: this.rmTranslationsEnabled,
          featureFlags,
        }),
      );
    }
  }
}
